import React, { ComponentType, ReactNode, useCallback, useLayoutEffect, useRef } from "react";

import FieldVisibilityChecker from "@mapmycustomers/shared/types/fieldModel/FieldVisibilityChecker";
import RangeModel from "@mapmycustomers/shared/types/viewModel/internalModel/RangeModel";

import ExtendedAnalytics from "@app/component/analytics/ExtendedAnalytics";
import { EntityItemProperties } from "@app/component/RecordCard";

import localSettings from "../../config/LocalSettings";

import List from "./components/List";
import NoData from "./components/NoData";
import Pagination from "./components/Pagination";
import RecordCount from "./components/RecordCount";
import RecordListSearch from "./components/RecordListSearch";
import styles from "./RecordList.module.scss";
import TextGetters from "./types/TextGetters";

export interface RecordListProps<T> {
  actions?: ReactNode;
  alerts?: ReactNode | ReactNode[];
  banner?: ReactNode;
  headerClassName?: string;
  idGetter: (item: T) => string;
  itemComponent: ComponentType<EntityItemProperties<T>>;
  loading?: boolean;
  middleActions?: ReactNode;
  noData?: ReactNode; // can be null to show no NoData
  onRangeChange?: (range: RangeModel) => void;
  onSearch?: (query: string) => void; // undefined value hides search
  overrideFieldVisibility?: FieldVisibilityChecker;
  pageSize: number;
  range?: RangeModel; // set some value to show pagination (total prop is also required)
  recordCountClassName?: string;
  records: T[];
  scrollPositionKey?: string;
  searchActions?: ReactNode;
  searchPlaceholder?: string;
  showCount?: boolean;
  textGetters: TextGetters;
  total?: number;
}

const RecordList = <T,>({
  actions,
  alerts,
  banner,
  headerClassName,
  idGetter,
  itemComponent,
  loading,
  middleActions,
  noData = <NoData />,
  onRangeChange,
  onSearch,
  overrideFieldVisibility,
  pageSize,
  range,
  recordCountClassName,
  records,
  scrollPositionKey,
  searchActions,
  searchPlaceholder,
  showCount = true,
  textGetters,
  total,
}: RecordListProps<T>) => {
  const ref = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (ref.current && scrollPositionKey) {
      setTimeout(() => {
        if (records.length > 0) {
          const scroll = localSettings.getRecordListScrollStateByKey(scrollPositionKey);
          ref.current?.scrollTo({ behavior: "smooth", top: scroll });
        }
      }, 0);
    }
  }, [records, range, scrollPositionKey]);

  const handleScroll = useCallback(
    ({ target: { scrollTop } }) => {
      if (records.length > 0 && scrollPositionKey && scrollTop !== undefined) {
        localSettings.setRecordListScrollStateByKey(scrollPositionKey, scrollTop);
      }
    },
    [records, scrollPositionKey]
  );

  return (
    <ExtendedAnalytics keyPath="Record List">
      <div className={styles.container}>
        <div className={headerClassName}>
          {showCount && total !== undefined && (
            <RecordCount
              actions={actions}
              className={recordCountClassName}
              count={textGetters.countTextGetter(total)}
            />
          )}

          {banner}
          {onSearch && (
            <RecordListSearch
              actions={searchActions}
              onSearch={onSearch}
              placeholder={searchPlaceholder}
            />
          )}
        </div>
        {middleActions}
        {alerts}
        <div className={styles.body} onScroll={handleScroll} ref={ref}>
          <List<T>
            idGetter={idGetter}
            itemComponent={itemComponent}
            loading={loading}
            noData={noData}
            overrideFieldVisibility={overrideFieldVisibility}
            records={records}
          />
        </div>
        {range && total !== undefined && textGetters.paginationTextGetter && (
          <Pagination
            count={records.length}
            loading={loading}
            onRangeChange={onRangeChange}
            pageSize={pageSize}
            range={range}
            textGetter={textGetters.paginationTextGetter}
            total={total}
          />
        )}
      </div>
    </ExtendedAnalytics>
  );
};

export default RecordList;
